import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Brain, FileText, Loader2, AlertTriangle, Scan, GitCompare,
  Clock, BarChart3, Lightbulb, Upload, Sparkles, Shield
} from 'lucide-react';
import { useCreditGate } from '../CreditGate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IntelligenceAnalyzer = () => {
  const { requestRun, dialog } = useCreditGate('enterprise_insight', 'Confirm Enterprise Analysis');
  const [text, setText] = useState('');
  const [docType, setDocType] = useState('invoice');
  const [loading, setLoading] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [results, setResults] = useState({});
  const token = localStorage.getItem('reasondms_token');

  const callApi = async (endpoint, body, label) => {
    setLoading(true);
    setActiveAction(label);
    try {
      const res = await fetch(`${API_URL}/api/enterprise/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        let errorMsg = `${label} failed`;
        try {
          const errData = await res.json();
          errorMsg = errData.detail || errData.message || errorMsg;
        } catch { errorMsg = res.status === 404 ? 'Enterprise AI service not available. Please check server configuration.' : `Server error (${res.status})`; }
        throw new Error(errorMsg);
      }
      const data = await res.json();
      setResults(prev => ({ ...prev, [endpoint.replace('/', '_')]: data }));
      toast.success(`${label} complete`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  };

  const runFullAnalysis = async () => {
    if (!text.trim()) { toast.error('Enter document text'); return; }
    const body = { text, doc_type: docType };
    setLoading(true);
    setActiveAction('Full Analysis');
    try {
      const endpoints = [
        ['summarize', { ...body, summary_type: 'executive' }, 'summarize'],
        ['risk/score', body, 'risk_score'],
        ['compliance/validate', body, 'compliance_validate'],
        ['anomaly/detect', body, 'anomaly_detect'],
        ['lifecycle/analyze', body, 'lifecycle_analyze'],
        ['quality/score', body, 'quality_score'],
      ];
      const newResults = {};
      for (const [ep, b, key] of endpoints) {
        const res = await fetch(`${API_URL}/api/enterprise/${ep}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(b)
        });
        if (res.ok) newResults[key] = await res.json();
      }
      setResults(newResults);
      toast.success('Full analysis complete');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  };

  const RiskBadge = ({ level }) => {
    const colors = { LOW: 'bg-emerald-100 text-emerald-700', MEDIUM: 'bg-amber-100 text-amber-700', HIGH: 'bg-red-100 text-red-700', CRITICAL: 'bg-red-200 text-red-800' };
    return <Badge className={`${colors[level] || 'bg-slate-100 text-slate-600'} text-xs`}>{level}</Badge>;
  };

  const ScoreBadge = ({ score, max = 100 }) => {
    const pct = Math.round((score / max) * 100);
    const color = pct >= 80 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-600';
    return <span className={`text-lg font-bold ${color}`}>{score}</span>;
  };

  return (
    <div className="space-y-4">
      {/* Input */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Document Text</label>
              <textarea
                data-testid="ei-text-input"
                value={text}
                onChange={e => setText(e.target.value)}
                className="w-full h-32 text-sm p-3 border rounded-lg font-mono resize-y focus:ring-2 focus:ring-cyan-300 focus:border-cyan-400 outline-none"
                placeholder="Paste document text here for analysis..."
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              data-testid="ei-doc-type-select"
              value={docType}
              onChange={e => setDocType(e.target.value)}
              className="text-sm border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-cyan-300 outline-none"
            >
              {['invoice','contract','purchase_order','receipt','bank_statement','tax_document','hr_document','legal_filing','medical_form','government_form','letter','report','general'].map(t => (
                <option key={t} value={t}>{t.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>
              ))}
            </select>
            <Button
              data-testid="ei-full-analysis-btn"
              onClick={() => requestRun(runFullAnalysis)}
              disabled={!text.trim() || loading}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Full Analysis
            </Button>
            <div className="flex gap-1.5 ml-auto">
              {[
                { ep: 'summarize', body: { text, doc_type: docType, summary_type: 'executive' }, label: 'Summary', icon: FileText },
                { ep: 'risk/score', body: { text, doc_type: docType }, label: 'Risk', icon: AlertTriangle },
                { ep: 'anomaly/detect', body: { text, doc_type: docType }, label: 'Fraud', icon: Scan },
                { ep: 'lifecycle/analyze', body: { text, doc_type: docType }, label: 'Lifecycle', icon: Clock },
              ].map(({ ep, body, label, icon: Icon }) => (
                <Button key={ep} variant="outline" size="sm" disabled={!text.trim() || loading}
                  data-testid={`ei-${label.toLowerCase()}-btn`}
                  onClick={() => callApi(ep, body, label)} className="text-xs">
                  <Icon className="h-3 w-3 mr-1" />
                  {activeAction === label ? <Loader2 className="h-3 w-3 animate-spin" /> : label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {Object.keys(results).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Summary */}
          {results.summarize?.success && (
            <Card data-testid="ei-summary-result">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4 text-cyan-600" /> Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-slate-700">{results.summarize.executive_summary}</p>
                {results.summarize.key_points?.length > 0 && (
                  <div>
                    <span className="text-xs font-medium text-slate-500">Key Points:</span>
                    <ul className="list-disc pl-4 mt-1">
                      {results.summarize.key_points.map((p, i) => <li key={i} className="text-xs text-slate-600">{p}</li>)}
                    </ul>
                  </div>
                )}
                {results.summarize.action_items?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {results.summarize.action_items.map((a, i) => <Badge key={i} variant="outline" className="text-xs">{a}</Badge>)}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Risk */}
          {results.risk_score?.success && (
            <Card data-testid="ei-risk-result">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Risk Assessment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <ScoreBadge score={results.risk_score.risk_score} />
                    <p className="text-xs text-slate-400">/100</p>
                  </div>
                  <RiskBadge level={results.risk_score.risk_level} />
                </div>
                <p className="text-xs text-slate-600">{results.risk_score.risk_summary}</p>
                {results.risk_score.risk_breakdown && (
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {Object.entries(results.risk_score.risk_breakdown).map(([k, v]) => (
                      <div key={k} className="text-center p-1.5 bg-slate-50 rounded">
                        <p className="text-xs text-slate-400 capitalize">{k}</p>
                        <p className="text-sm font-medium">{v}%</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Compliance */}
          {results.compliance_validate?.success && (
            <Card data-testid="ei-compliance-result">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Shield className="h-4 w-4 text-blue-600" /> Compliance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-4">
                  <ScoreBadge score={results.compliance_validate.compliance_score} />
                  <Badge className={results.compliance_validate.overall_status === 'COMPLIANT' ? 'bg-emerald-100 text-emerald-700' : results.compliance_validate.overall_status === 'NON_COMPLIANT' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                    {results.compliance_validate.overall_status}
                  </Badge>
                  <span className="text-xs text-slate-400">{results.compliance_validate.passed}/{results.compliance_validate.total_rules_checked} passed</span>
                </div>
                {results.compliance_validate.violations?.filter(v => v.status === 'FAIL').length > 0 && (
                  <div className="space-y-1">
                    {results.compliance_validate.violations.filter(v => v.status === 'FAIL').map((v, i) => (
                      <div key={i} className="text-xs p-2 bg-red-50 rounded flex items-center gap-2">
                        <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />
                        <span className="text-red-700">{v.rule_name}: {v.details}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Fraud */}
          {results.anomaly_detect?.success && (
            <Card data-testid="ei-fraud-result">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Scan className="h-4 w-4 text-purple-600" /> Fraud Detection</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-3">
                  <RiskBadge level={results.anomaly_detect.fraud_risk_level} />
                  <span className="text-xs text-slate-500">Score: {results.anomaly_detect.fraud_score}/100</span>
                  <span className="text-xs text-slate-500">{results.anomaly_detect.anomaly_count} anomalies</span>
                </div>
                <p className="text-xs text-slate-600">{results.anomaly_detect.assessment_summary}</p>
              </CardContent>
            </Card>
          )}

          {/* Lifecycle */}
          {results.lifecycle_analyze?.success && (
            <Card data-testid="ei-lifecycle-result">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Clock className="h-4 w-4 text-teal-600" /> Lifecycle</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Badge variant="outline" className="text-xs capitalize">{results.lifecycle_analyze.document_status}</Badge>
                {results.lifecycle_analyze.alerts?.length > 0 && (
                  <div className="space-y-1">
                    {results.lifecycle_analyze.alerts.map((a, i) => (
                      <div key={i} className={`text-xs p-2 rounded ${a.priority === 'high' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                        {a.message}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quality */}
          {results.quality_score?.success && (
            <Card data-testid="ei-quality-result">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-indigo-600" /> Quality</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-3">
                  <ScoreBadge score={results.quality_score.quality_score} />
                  <Badge variant="outline" className="text-xs">Grade: {results.quality_score.grade}</Badge>
                </div>
                {results.quality_score.issues?.length > 0 && (
                  <div className="text-xs text-slate-500">Issues: {results.quality_score.issues.join(', ')}</div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
      {dialog}
    </div>
  );
};

export default IntelligenceAnalyzer;
